import { useMemo, useState } from 'react';

import { FormikHelpers } from 'formik';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { DailyRewardEditModal } from './DailyRewardEditModal';
import { DailyRewardForm, DAILY_REWARD_FORM_INITIAL_VALUES, DailyRewardFormValues } from './DailyRewardForm';
import { getDailyRewardCardItems } from './utils';

import css from './DailyReward.module.css';

import { dailyRewardsService } from 'v2/api/dailyRewardsService';
import { rewardsService } from 'v2/api/rewardsService';
import { DailyBonusesCards } from 'v2/components/bonusesCards';
import { Card } from 'v2/components/card/Card';
import { handleHttpError } from 'v2/utils';

export const DailyReward = () => {
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const [currentDailyRewardId, setCurrentDailyRewardId] = useState<string>('');

  const {
    data: dailyRewards = [],
    isLoading: isDailyRewardsLoading,
    mutate: revalidateDailyRewards,
  } = useSWR('getDailyRewards', dailyRewardsService.getDailyRewards);

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerCreateDailyReward, isMutating: isCreatingDailyReward } = useSWRMutation(
    'createDailyReward',
    dailyRewardsService.createDailyReward
  );

  const { trigger: triggerDeleteDailyReward } = useSWRMutation(
    'deleteDailyReward',
    dailyRewardsService.deleteDailyReward
  );

  const onSubmit = async (formValues: DailyRewardFormValues, { resetForm }: FormikHelpers<DailyRewardFormValues>) => {
    const { day, description, allowedUserRoles, rewards } = formValues;

    try {
      const createdRewardIds = await triggerCreateRewards(rewards.map(({ type, value }) => ({ type, value })));

      await triggerCreateDailyReward({
        day,
        rewardIds: createdRewardIds,
        description,
        allowedUserRoles,
      });

      toast.success('Daily reward has been successfully created');
      await revalidateDailyRewards();
      resetForm();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleDailyRewardDelete = async (dailyRewardId: string) => {
    try {
      await triggerDeleteDailyReward(dailyRewardId);
      toast.success('Daily reward has been successfully deleted');
      await revalidateDailyRewards();

      if (currentDailyRewardId === dailyRewardId) {
        handleEditModelClose();
      }
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleEditModelOpen = (dailyRewardId: string) => {
    setCurrentDailyRewardId(dailyRewardId);

    setIsEditModalOpened(true);
  };

  const handleEditModelClose = () => {
    setCurrentDailyRewardId('');

    setIsEditModalOpened(false);
  };

  const bonusesCardItems = useMemo(() => getDailyRewardCardItems(dailyRewards), [dailyRewards]);
  const currentDailyReward = dailyRewards.find((dailyReward) => dailyReward.id === currentDailyRewardId) ?? null;
  const isDailyRewardProcessing = isCreatingRewards || isCreatingDailyReward;

  return (
    <div className={css.dailyRewardContainer}>
      <Typography margin="10px" fontSize="24px" fontWeight={600}>
        Daily Reward
      </Typography>

      <div className={css.dailyRewardLayout}>
        <Card className={css.dailyRewardFormColumn}>
          <Typography fontSize="18px" fontWeight={600}>
            Create Daily Reward
          </Typography>

          <DailyRewardForm
            submitButtonCaption="Create"
            initialValues={DAILY_REWARD_FORM_INITIAL_VALUES}
            isSubmitting={isDailyRewardProcessing}
            onSubmit={onSubmit}
          />
        </Card>

        <Box className={css.dailyRewardListColumn}>
          <DailyBonusesCards
            cardsTitle="All Daily Rewards"
            items={bonusesCardItems}
            openDeleteModal={handleDailyRewardDelete}
            openEditModal={handleEditModelOpen}
            isItemsLoading={isDailyRewardsLoading}
            deleteConfirmationMessage="Are you sure that you want to delete this daily reward?"
          />
        </Box>
      </div>

      <DailyRewardEditModal
        dailyReward={currentDailyReward}
        handleClose={handleEditModelClose}
        isOpened={isEditModalOpened}
        onRevalidate={revalidateDailyRewards}
      />
    </div>
  );
};
